import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { useSelector } from "react-redux";
import CustomeButton from "~/components/Button";
import AdminOnly from "~/components/AdminOnly";
import PermissionGate from "~/components/PermissionGate";
import FileUploadModal from "~/components/Processes/conversations/FileUploadModal";
import Notification from "~/components/entities/notification/Notification";

import useAttachments from "../../actions/useAttachments";
import AttachmentTable from "./AttachmentTable";
import TemporaryNotification from "~/components/toast-notification/AttachmentTemporaryNotification";
import DocumentConfirmationDialog from "~/components/confirmation-dialog/DocumentConfirmationDialog";
import AttachmentSuccess from "~/components/toast-notification/AttachmentSuccess";
import AttachmentConfirmDialog from "~/components/AttachmentConfirmationModal/AttachmentConfirmDialog";
import AuditSidebar from "~/components/audit-sidebar/AuditSidebar";
import API_ROUTES_V2 from "~/api-routes-v2";
import categories from "../../../../assets/categories-subcategories.json";
import { mergeCategories } from "~/Pages/Requests/components/new-request-modal/utils";
import { useEffect, useRef, useState } from "react";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";
import BlobFilter from "~/components/BlobFilter";
import FilterChip from "~/components/FilterChip";

const EmployeeAttachments = () => {
  const { id } = useParams();
  const { role } = useSelector((state) => state.login);
  const entityId = useSelector(
    (state) => state.globalEntities?.entity_detail?.id,
  );

  const {
    state,
    setShowUploadModal,
    getNameForFile,
    uploadFileHandler,
    handleChangePage,
    handleDeleteDocument,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    setSuccessMessage,
    handleRestoreAttachment,
    setTemporaryMessage,
    setRstoreMessage,
    setShowAttachmentConfirmation,
    setFiles,
    setAuditSidebarVisible,
    selectedItems,
    filterData,
    setFilterData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    data,
    filterLoading,
    onSortOrderChange,
    onFilterApply,
    allData,
    filterPageChange,
    currentPage,
    fetchEmployeeAttachments,
  } = useAttachments();
  const auditLabels = () => {
    return {
      create: "File Uploaded",
      update: "File Updated",
      delete: "File(s) Deleted",
    };
  };

  return (
    <>
      <Card className="card-onboard attachment-card">
        <div className="flex justify-content-between align-items-center mb-5">
          <p className="heading m-0 mb-2">Attachments</p>
          <div className="flex gap-2">
            <AdminOnly>
              <CustomeButton
                label="View History"
                icon="pi pi-history"
                iconPos="right"
                onClick={() => setAuditSidebarVisible(true)}
              />
            </AdminOnly>
            <BlobFilter
              onApply={({ value, group }) => {
                setFilterData(value);
              }}
              onRemove={(fn) => (removeFnRef.current = fn)}
              onFilterChange={({ value, grouped }) => {
                handleFilterChange(grouped);
              }}
              showEntity={false}
              showServiceLine={false}
              showYears={true}
              serviceLine={"Payroll"}
              disabled={!state?.isFileAvailable}
            />
            <PermissionGate
              resource="employee"
              action="modify"
              resourceId={id}
              resourceAttr={{ entity_id: entityId }}
              skip={!entityId}
            >
              <CustomeButton
                label="Upload File"
                variant="contained"
                icon="pi pi-plus"
                iconPos="right"
                onClick={() => {
                  setShowUploadModal(true);
                }}
              />
            </PermissionGate>
          </div>
        </div>

        <FilterChip
          filterData={filterData}
          removeFilter={(id) => {
            handleRemoveFilter(id);
          }}
        />
        <Notification
          successMessage={state?.successMessage}
          setSuccessMessage={setSuccessMessage}
        />
        <TemporaryNotification
          onUndo={handleRestoreAttachment}
          successMsg={state?.temporaryMessage}
          setMessages={setTemporaryMessage}
        />
        <AttachmentSuccess
          successMsg={state?.restoreMessage}
          setMessages={setRstoreMessage}
        />
        <div className="formgrid grid mt-4">
          <div className="col-12">
            <AttachmentTable
              files={selectedItems.length ? data : state?.filesData}
              isDataAvailable={
                selectedItems.length ? allData.length : state?.isFileAvailable
              }
              loading={selectedItems.length ? filterLoading : state?.loading}
              first={selectedItems.length ? currentPage - 1 : state?.first}
              totalRecords={
                selectedItems.length ? allData.length : state?.filesData.length
              }
              rows={state?.rows}
              onPageChange={
                selectedItems.length
                  ? (e) => {
                      filterPageChange(e.page + 1);
                    }
                  : handleChangePage
              }
              setOpenDialog={setShowUploadModal}
              handleAttachmentDelete={handleDeleteDocument}
              onSortOrderChange={
                selectedItems?.length ? onSortOrderChange : undefined
              }
              isFilterApplied={selectedItems.length}
              onRefetch={
                selectedItems?.length ? onFilterApply : fetchEmployeeAttachments
              }
            />
          </div>
        </div>

        <FileUploadModal
          entitydata={state?.entityData}
          showModal={state?.showUploadModal}
          fileSupported={state?.fileSupported}
          hideUploadModal={() => setShowUploadModal(false)}
          setUploadedFile={(files) => {
            if (["LSP_USER", "LSP_MANAGER"].includes(role)) {
              setShowAttachmentConfirmation(true);
              setFiles(files);
            } else {
              uploadFileHandler(files);
            }
          }}
          getService={getNameForFile}
          placeholder={"Choose a file to upload"}
          loading={state?.isFileUploaded}
          close={false}
          employeeData={state?.employeeDetails}
          isEmployeeAttachment={true}
          categories={mergeCategories(["Payroll"], role)}
        />
        {state?.attachmentConfirmModal && (
          <DocumentConfirmationDialog
            visible={state?.attachmentConfirmModal}
            closeModal={() => setAttachmentConfirmModal(false)}
            handleSave={handleConfirmDelete}
            isLoader={state?.isAttachmentLoader}
          />
        )}
        {state?.showAttachmentConfirmation && (
          <AttachmentConfirmDialog
            showModal={state?.showAttachmentConfirmation}
            handleCloseModal={() => setShowAttachmentConfirmation(false)}
            handleConfirm={() => {
              uploadFileHandler(state.files);
              setShowAttachmentConfirmation(false);
            }}
            message="Please double check that you are uploading the correct document. Do not share private or personal information with the wrong recipient."
          />
        )}
        {state.isAuditSidebarVisible && (
          <AuditSidebar
            heading="Attachments"
            endpoint={API_ROUTES_V2.FETCH_ATTACHMENT_AUDIT}
            id={id}
            objectType="EMPLOYEE"
            setSidebarToggle={setAuditSidebarVisible}
            sidebarToggle={state.isAuditSidebarVisible}
            labels={auditLabels}
          />
        )}
      </Card>
    </>
  );
};
export default EmployeeAttachments;
